/* ==========================================================================
   Kintsugi — storefront behaviour
   Cart, navigation, variant selection, disclosure widgets, affiliate capture.
   No framework. Custom elements where state is genuinely per-instance.
   ========================================================================== */

(function () {
  'use strict';

  var T = window.theme || {};
  var routes = T.routes || {};

  /* ----------------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------------- */

  function money(cents) {
    // Server-rendered prices are authoritative everywhere except the optimistic
    // cart-drawer update, which is the only place this is used.
    var fmt = T.moneyFormat || '${{amount}}';
    var amount = (cents / 100);

    function withDelimiters(value, precision, thousands, decimal) {
      var parts = value.toFixed(precision).split('.');
      var int = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? int + decimal + parts[1] : int;
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
      switch (name) {
        case 'amount': return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(amount, 0, ',', '');
        case 'amount_with_comma_separator': return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_period_and_space_separator': return withDelimiters(amount, 2, ' ', ',');
        default: return withDelimiters(amount, 2, ',', '.');
      }
    });
  }

  function trapFocus(container, opener) {
    var selector = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

    function onKey(e) {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = container.querySelectorAll(selector);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function close() {
      container.dispatchEvent(new CustomEvent('trap:escape'));
    }

    container.addEventListener('keydown', onKey);
    var firstFocusable = container.querySelector(selector);
    if (firstFocusable) firstFocusable.focus();

    return function release() {
      container.removeEventListener('keydown', onKey);
      if (opener && document.body.contains(opener)) opener.focus();
    };
  }

  function lockScroll(on) {
    if (on) {
      // Preserve the scroll position; position:fixed on body would jump it.
      document.body.dataset.scrollLock = String(window.scrollY);
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight =
        (window.innerWidth - document.documentElement.clientWidth) + 'px';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      delete document.body.dataset.scrollLock;
    }
  }

  /* ----------------------------------------------------------------------
     Affiliate attribution

     The author wants to pay influencers a share of what sells through their
     links. Shopify's own discount codes cannot attribute a sale that used no
     code, so the referrer is captured into a cart attribute: it survives to the
     order and shows in the admin, which is enough to reconcile payouts.
     A real affiliate app should replace this if volume justifies one.
     ---------------------------------------------------------------------- */

  var Affiliate = {
    key: 'kintsugi_ref',

    init: function () {
      var cfg = (T.settings && T.settings.affiliate) || {};
      if (!cfg.enabled) return;

      var param = cfg.param || 'ref';
      var value = new URLSearchParams(window.location.search).get(param);

      if (value) {
        try {
          localStorage.setItem(
            this.key,
            JSON.stringify({
              ref: value.slice(0, 64),
              at: Date.now(),
              days: cfg.days || 30,
              landing: window.location.pathname
            })
          );
        } catch (e) {}
      }

      var stored = this.read();
      if (stored) this.attach(stored);
    },

    read: function () {
      try {
        var raw = localStorage.getItem(this.key);
        if (!raw) return null;
        var data = JSON.parse(raw);
        var maxAge = (data.days || 30) * 86400000;
        if (Date.now() - data.at > maxAge) {
          localStorage.removeItem(this.key);
          return null;
        }
        return data;
      } catch (e) {
        return null;
      }
    },

    attach: function (data) {
      // Only write once per session; every write is a network round trip.
      if (sessionStorage.getItem(this.key + '_sent') === data.ref) return;
      fetch(routes.cartUpdate || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            Referido: data.ref,
            'Referido origen': data.landing
          }
        })
      })
        .then(function () {
          try { sessionStorage.setItem(Affiliate.key + '_sent', data.ref); } catch (e) {}
        })
        .catch(function () {});
    }
  };

  /* ----------------------------------------------------------------------
     Cart
     ---------------------------------------------------------------------- */

  var Cart = {
    subscribers: [],

    on: function (fn) { this.subscribers.push(fn); },

    publish: function (cart) {
      this.subscribers.forEach(function (fn) {
        try { fn(cart); } catch (e) {}
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    },

    get: function () {
      return fetch((routes.cart || '/cart') + '.js', {
        headers: { Accept: 'application/json' }
      }).then(function (r) { return r.json(); });
    },

    add: function (items) {
      return fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: Array.isArray(items) ? items : [items] })
      })
        .then(function (r) {
          return r.json().then(function (body) {
            if (!r.ok) throw body;
            return body;
          });
        })
        .then(
          function (body) {
            return Cart.get().then(function (cart) {
              Cart.publish(cart);
              return body;
            });
          }
        );
    },

    change: function (line, quantity) {
      return fetch(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          Cart.publish(cart);
          return cart;
        });
    }
  };

  /* ----------------------------------------------------------------------
     <cart-drawer>
     ---------------------------------------------------------------------- */

  if ('customElements' in window) {
    customElements.define(
      'cart-drawer',
      class CartDrawer extends HTMLElement {
        connectedCallback() {
          this.release = null;
          this.opener = null;

          this.addEventListener('click', (e) => {
            if (e.target.closest('[data-drawer-close]') || e.target.matches('.drawer__overlay')) {
              this.close();
            }
            var remove = e.target.closest('[data-line-remove]');
            if (remove) {
              e.preventDefault();
              this.setLine(remove.dataset.line, 0);
            }
          });

          this.addEventListener('trap:escape', () => this.close());

          Cart.on((cart) => this.render(cart));

          document.addEventListener('cart:open', (e) => this.open(e.detail && e.detail.opener));
        }

        open(opener) {
          this.opener = opener || null;
          this.dataset.open = 'true';
          lockScroll(true);
          this.release = trapFocus(this.querySelector('.drawer__panel'), this.opener);
        }

        close() {
          if (this.dataset.open !== 'true') return;
          this.dataset.open = 'false';
          lockScroll(false);
          if (this.release) {
            this.release();
            this.release = null;
          }
        }

        setLine(line, quantity) {
          this.setAttribute('aria-busy', 'true');
          Cart.change(parseInt(line, 10), quantity).finally(() => {
            this.removeAttribute('aria-busy');
          });
        }

        render(cart) {
          // Re-fetch the rendered drawer rather than templating in JS: the
          // server already knows how to format money, discounts and unit
          // prices for every market the shop sells into.
          fetch(window.location.pathname + '?sections=cart-drawer', {
            headers: { Accept: 'application/json' }
          })
            .then((r) => r.json())
            .then((data) => {
              var html = data['cart-drawer'];
              if (!html) return;
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var fresh = doc.querySelector('[data-drawer-contents]');
              var target = this.querySelector('[data-drawer-contents]');
              if (fresh && target) target.innerHTML = fresh.innerHTML;
            })
            .catch(() => {});
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
     Cart count badges
     ---------------------------------------------------------------------- */

  Cart.on(function (cart) {
    var badges = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = cart.item_count;
      badges[i].hidden = cart.item_count === 0;
    }
  });

  /* ----------------------------------------------------------------------
     Add-to-cart forms
     ---------------------------------------------------------------------- */

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-product-form]');
    if (!form) return;
    if (!T.settings || !T.settings.cartDrawer) return; // let it post normally

    e.preventDefault();

    var button = form.querySelector('[data-add-to-cart]');
    var errorEl = form.querySelector('[data-form-error]');
    var idInput = form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return;

    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.dataset.loading = 'true';
    }
    if (errorEl) errorEl.textContent = '';

    var qtyInput = form.querySelector('[name="quantity"]');
    var payload = {
      id: parseInt(idInput.value, 10),
      quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1
    };

    // Selling-plan and line-item properties, if the form carries them.
    var plan = form.querySelector('[name="selling_plan"]');
    if (plan && plan.value) payload.selling_plan = parseInt(plan.value, 10);

    var props = {};
    form.querySelectorAll('[name^="properties["]').forEach(function (input) {
      var match = input.name.match(/^properties\[(.+)\]$/);
      if (match && input.value) props[match[1]] = input.value;
    });
    if (Object.keys(props).length) payload.properties = props;

    // Cart attributes (cédula) are not line-item properties. /cart/add.js
    // ignores them, so they go to /cart/update.js before the line is added.
    var attrs = {};
    form.querySelectorAll('[name^="attributes["]').forEach(function (input) {
      var match = input.name.match(/^attributes\[(.+)\]$/);
      if (match && input.value) attrs[match[1]] = input.value;
    });

    var pending = Cart.add(payload);
    if (Object.keys(attrs).length) {
      pending = fetch((T.routes && T.routes.cartUpdate) || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function (r) {
        if (!r.ok) throw new Error('No se pudo guardar la cédula');
        return Cart.add(payload);
      });
    }

    pending
      .then(function () {
        document.dispatchEvent(
          new CustomEvent('cart:open', { detail: { opener: button } })
        );
      })
      .catch(function (err) {
        var message = (err && (err.description || err.message)) || T.strings.quantityError;
        if (errorEl) errorEl.textContent = message;
      })
      .finally(function () {
        if (button) {
          button.removeAttribute('aria-disabled');
          delete button.dataset.loading;
        }
      });
  });

  /* ----------------------------------------------------------------------
     Header cart button
     ---------------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-cart-open]');
    if (!trigger) return;
    if (!T.settings || !T.settings.cartDrawer) return;
    e.preventDefault();
    document.dispatchEvent(new CustomEvent('cart:open', { detail: { opener: trigger } }));
  });

  /* ----------------------------------------------------------------------
     Mobile navigation
     ---------------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var open = e.target.closest('[data-nav-open]');
    var close = e.target.closest('[data-nav-close]');
    var drawer = document.querySelector('[data-nav-drawer]');
    if (!drawer) return;

    if (open) {
      drawer.dataset.open = 'true';
      lockScroll(true);
      drawer.__release = trapFocus(drawer, open);
    } else if (close) {
      drawer.dataset.open = 'false';
      lockScroll(false);
      if (drawer.__release) {
        drawer.__release();
        drawer.__release = null;
      }
    }
  });

  document.addEventListener('trap:escape', function (e) {
    var drawer = e.target.closest('[data-nav-drawer]');
    if (!drawer) return;
    drawer.dataset.open = 'false';
    lockScroll(false);
    if (drawer.__release) {
      drawer.__release();
      drawer.__release = null;
    }
  });

  /* ----------------------------------------------------------------------
     <variant-picker> — keeps price, availability and the URL in step.
     ---------------------------------------------------------------------- */

  if ('customElements' in window) {
    customElements.define(
      'variant-picker',
      class VariantPicker extends HTMLElement {
        connectedCallback() {
          this.form = this.closest('form[data-product-form]');
          try {
            this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
          } catch (e) {
            this.variants = [];
          }
          this.addEventListener('change', () => this.onChange());
        }

        get selectedOptions() {
          return Array.from(this.querySelectorAll('input:checked')).map(function (i) {
            return i.value;
          });
        }

        onChange() {
          var chosen = this.selectedOptions;
          var match = this.variants.find(function (v) {
            return v.options.every(function (opt, i) { return opt === chosen[i]; });
          });

          this.updateAvailability(chosen);

          if (!match) {
            this.setUnavailable();
            return;
          }

          var idInput = this.form && this.form.querySelector('[name="id"]');
          if (idInput) idInput.value = match.id;

          if (history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', match.id);
            history.replaceState({}, '', url.toString());
          }

          this.renderPrice(match);
          this.renderButton(match);
          this.renderMedia(match);
        }

        // Grey out combinations that do not exist, so a reader never picks a
        // format the shop cannot sell them.
        updateAvailability(chosen) {
          var groups = this.querySelectorAll('[data-option-index]');
          var variants = this.variants;
          groups.forEach(function (group) {
            var index = parseInt(group.dataset.optionIndex, 10);
            group.querySelectorAll('input').forEach(function (input) {
              var probe = chosen.slice();
              probe[index] = input.value;
              var exists = variants.some(function (v) {
                for (var i = 0; i < probe.length; i++) {
                  if (i === index) {
                    if (v.options[i] !== probe[i]) return false;
                  } else if (probe[i] && v.options[i] !== probe[i]) {
                    return false;
                  }
                }
                return v.available;
              });
              input.disabled = !exists;
            });
          });
        }

        renderPrice(variant) {
          var host = document.querySelector('[data-price-target]');
          if (!host) return;
          var out = '<span class="product__price">' + money(variant.price) + '</span>';
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            out = '<del>' + money(variant.compare_at_price) + '</del>' + out;
          }
          host.innerHTML = out;
        }

        renderButton(variant) {
          var button = this.form && this.form.querySelector('[data-add-to-cart]');
          if (!button) return;
          var label = button.querySelector('[data-add-label]') || button;
          if (variant.available) {
            button.removeAttribute('disabled');
            label.textContent = button.dataset.labelDefault || T.strings.addedToCart;
          } else {
            button.setAttribute('disabled', 'disabled');
            label.textContent = T.strings.soldOut;
          }
        }

        setUnavailable() {
          var button = this.form && this.form.querySelector('[data-add-to-cart]');
          if (!button) return;
          button.setAttribute('disabled', 'disabled');
          var label = button.querySelector('[data-add-label]') || button;
          label.textContent = T.strings.unavailable;
        }

        renderMedia(variant) {
          if (!variant.featured_media) return;
          var thumb = document.querySelector(
            '[data-media-id="' + variant.featured_media.id + '"]'
          );
          if (thumb) thumb.click();
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
     Product gallery
     ---------------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-media-id]');
    if (!thumb) return;
    var gallery = thumb.closest('[data-gallery]');
    if (!gallery) return;

    gallery.querySelectorAll('[data-media-id]').forEach(function (t) {
      t.setAttribute('aria-current', String(t === thumb));
    });

    var main = gallery.querySelector('[data-gallery-main]');
    var source = thumb.querySelector('img');
    if (main && source) {
      var img = main.querySelector('img');
      if (img) {
        img.src = source.dataset.large || source.src;
        img.srcset = source.dataset.largeSrcset || '';
        img.alt = source.alt;
      }
    }
  });

  /* ----------------------------------------------------------------------
     Quantity steppers
     ---------------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var button = e.target.closest('[data-qty-step]');
    if (!button) return;
    var wrap = button.closest('.qty');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;
    var step = parseInt(button.dataset.qtyStep, 10);
    var min = parseInt(input.min || '1', 10);
    var max = input.max ? parseInt(input.max, 10) : Infinity;
    var next = (parseInt(input.value, 10) || min) + step;
    input.value = Math.max(min, Math.min(max, next));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ----------------------------------------------------------------------
     Cart page quantity — apply the stepper without making the reader hunt for
     the Update button. The form posts normally, so this still works with JS
     disabled via that button.
     ---------------------------------------------------------------------- */

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty]');
    if (!input || !input.form) return;
    if (typeof input.form.requestSubmit === 'function') input.form.requestSubmit();
    else input.form.submit();
  });

  /* ----------------------------------------------------------------------
     Disclosures — FAQ and the excerpt reader.
     Height is animated explicitly because `height: auto` is not animatable and
     a max-height guess either clips long answers or eases wrong.
     ---------------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-disclosure]');
    if (!trigger) return;

    var panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;

    var open = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!open));

    if (open) {
      panel.style.height = panel.scrollHeight + 'px';
      requestAnimationFrame(function () { panel.style.height = '0px'; });
    } else {
      panel.style.height = panel.scrollHeight + 'px';
      panel.addEventListener('transitionend', function done(ev) {
        if (ev.propertyName !== 'height') return;
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', done);
      });
    }
  });

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-excerpt-toggle]');
    if (!trigger) return;
    var excerpt = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!excerpt) return;
    var open = excerpt.dataset.open === 'true';
    excerpt.style.setProperty('--excerpt-open-h', excerpt.scrollHeight + 'px');
    excerpt.dataset.open = String(!open);
    trigger.setAttribute('aria-expanded', String(!open));
    var label = trigger.querySelector('[data-label]') || trigger;
    label.textContent = open ? trigger.dataset.labelOpen : trigger.dataset.labelClose;
    if (open) {
      excerpt.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  });

  /* ----------------------------------------------------------------------
     Address country select

     `country_option_tags` emits every country but marks none as selected, so a
     saved address would otherwise reopen showing the wrong country.
     ---------------------------------------------------------------------- */

  function initCountrySelects(scope) {
    (scope || document).querySelectorAll('select[data-default]').forEach(function (select) {
      var wanted = select.dataset.default;
      if (!wanted) return;
      select.value = wanted;
    });
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  Affiliate.init();
  initCountrySelects(document);
  document.addEventListener('shopify:section:load', function (e) {
    initCountrySelects(e.target);
  });

  // Keep the badge honest on back/forward navigation out of a cached page.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) Cart.get().then(function (c) { Cart.publish(c); });
  });

  window.Cart = Cart;
})();
